import React, { memo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { translateBlock } from 'i18n';

import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import TooltipIconButton from 'components/TooltipIconButton';
import { FilterOperations as FO } from 'business/definitions';
import { transformUrlParams } from 'helpers/urlHelpers';
import { camelize, snakify } from 'helpers/syntaxHelper';
import { getFilterManager } from 'components/Shared/filters/helpers';
import { BudgetBys } from 'business/constants';
import { useGetScopes } from 'routes/BudgetModule/shared/useGetScopes';
import { isAllExpression } from 'helpers/scopes';
import { FINANCE_SCOPE } from 'routes/BudgetModule/config/constants';

type Props = {
    filters: SentenceFilterWithScopesT[];
    updateFilter: (newFilter: Partial<SentenceFilterT>) => void;
    budgetBy?: BudgetBys;
};

const StyWrapper = styled(Box, {
    name: 'StyWrapper',
})({
    flex: 1,
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    maxWidth: '100%',
    width: 'inherit',
    margin: '1rem',
});

const StyFilter = styled('fieldset', {
    name: 'StyFilter',
})(({ theme }) => ({
    border: `thin solid ${theme.palette.primary.main}`,
    borderRadius: '28px',
    padding: '0.5rem 1rem',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    margin: '0.3rem 0.8rem',
}));

const StyTypography = styled(Typography, {
    name: 'StyTypography',
})(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: '0.6rem',
}));

const StyTooltipIcon = styled(TooltipIconButton, {
    name: 'StyTooltipIcon',
})({
    padding: '0.5rem',
    marginLeft: '1rem',
});

const BudgetFilters = ({
    filters,
    updateFilter,
    budgetBy = undefined,
}: Props): React.ReactElement => {
    const { t } = useTranslation('components');
    const scopes = useGetScopes();
    const financeScope = scopes.find((scope) => scope.name === FINANCE_SCOPE);
    const isAllScope = financeScope
        ? isAllExpression(financeScope?.expression)
        : false;

    const resetFilterMaker = (field: string) => () => {
        const filter = filters.find((f) => f.field === field);
        const nullFilter: SentenceFilterT = {
            // TODO StrictNullCheck : Try to remove "!"
            ...filter!,
            value: null,
            valueScale: null,
            operation:
                // TODO StrictNullCheck : Try to remove "!"
                filter!.operation === FO.in_
                    ? filter!.operation
                    : (FO.greater as FilterOperationsT),
        };
        if (filter) {
            updateFilter(nullFilter);
        }
    };

    const onFilterValueChange = (
        filter: SentenceFilterT,
        newFilterData: Partial<SentenceFilterT>,
    ): void => {
        const newFilter = {
            ...filter,
            ...newFilterData,
        };
        updateFilter(newFilter);
    };

    if (_.isEmpty(filters)) return <></>;

    return (
        <StyWrapper>
            {_(filters)
                .map((filter, index) => {
                    const FilterPickerItem = getFilterManager(filter, false);
                    const filterTitle = translateBlock(camelize(filter.field));
                    const isEmptyFilter =
                        filter.value == null || filter.value === '';
                    // We filter only if it's the BP ventilation or if our scope are in ALL.
                    // The split is done due to the filter field having either the table or a field name.
                    const shouldFilterValuesByScope =
                        filter.field.split('.')[0] ===
                            budgetBy?.split('.')[0] || isAllScope;
                    return (
                        <StyFilter key={`${filter.value} ${filter.field}`}>
                            <legend>
                                <StyTypography>{filterTitle}</StyTypography>
                            </legend>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'right',
                                    marginBottom: '2px',
                                    width: '100%',
                                }}
                            >
                                <FilterPickerItem
                                    filter={filter}
                                    shouldFilterValuesByScope={
                                        shouldFilterValuesByScope
                                    }
                                    setFilter={(
                                        newFilterData: Partial<SentenceFilterT>,
                                    ) =>
                                        onFilterValueChange(
                                            filter,
                                            newFilterData,
                                        )
                                    }
                                    displayIndex={index}
                                    isPerimeterFilter
                                    preProcessingUrlCallback={(url: string) =>
                                        transformUrlParams(
                                            url,
                                            ['filter'],
                                            snakify,
                                        )
                                    }
                                />
                                {!isEmptyFilter && (
                                    <StyTooltipIcon
                                        tooltip={t(
                                            'queryPerimeter.clearFilter',
                                        )}
                                        onClick={resetFilterMaker(filter.field)}
                                        icon={
                                            <CloseIcon
                                                style={{
                                                    fontSize: '1rem',
                                                }}
                                            />
                                        }
                                    />
                                )}
                            </div>
                        </StyFilter>
                    );
                })
                .value()}
        </StyWrapper>
    );
};

export default memo(BudgetFilters);
